import { type Dispatch, useCallback, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import type { SingleBuildingFormState } from "../../../model/types";
import type { HandleSubmitBase, SubmitCaller } from "../types";
import type { SubmitInfo, SubmitInfoAction } from "../types";

import { uploadFilesAndImages } from "../../../api/upload";
import { SingleBuildingContext } from "../../../context/AddSingleBuildingContext";

type UseUploadAndSubmitParams = {
  deleteImages: () => void;
  dispatch: Dispatch<SubmitInfoAction>;
  setIsSubmitting: (isSubmitting: boolean) => void;
  setJobId: (jobId: string) => void;
  shouldAbortSubmitCall: () => boolean | undefined;
  submitInfoDefault: SubmitInfo;
  userId: string;
};

export const useUploadAndSubmit = ({
  deleteImages,
  dispatch,
  setIsSubmitting,
  setJobId,
  shouldAbortSubmitCall,
  submitInfoDefault,
  userId,
}: UseUploadAndSubmitParams) => {
  const { t } = useTranslation();
  const { formData, setFormData } = useContext(SingleBuildingContext);
  const submitAbortRef = useRef<AbortController | null>(null);

  const cancelInFlightUploads = useCallback(() => {
    submitAbortRef.current?.abort();
    submitAbortRef.current = null;
  }, []);

  const prepareSubmit = useCallback(() => {
    setIsSubmitting(true);
    const submitDateTime = new Date();
    submitAbortRef.current?.abort();
    const ac = new AbortController();
    submitAbortRef.current = ac;
    dispatch({ base: submitInfoDefault, formData, type: "PREPARE_SUBMIT" });
    return { signal: ac.signal, submitDateTime };
  }, [dispatch, submitInfoDefault, formData, setIsSubmitting]);

  const uploadFiles = useCallback(
    async (signal: AbortSignal, submitDateTime: Date) => {
      let formDataWithUrls: SingleBuildingFormState | undefined;
      try {
        formDataWithUrls = await uploadFilesAndImages({
          formData,
          onUploadRowProgress: (list, file, progress) => {
            dispatch({
              errorMessage: progress.errorMessage,
              file,
              list,
              percentage: progress.percentage,
              status: progress.status,
              type: "UPSERT_UPLOAD_ROW",
            });
          },
          signal,
          submitDateTime,
          userId,
        });

        setFormData(formDataWithUrls);
      } catch {
        // ignore error
      }
      return formDataWithUrls;
    },
    [dispatch, formData, setFormData, userId]
  );

  const submitToApi = useCallback(
    async (formDataToSubmit: SingleBuildingFormState, dtCreation: boolean, caller: SubmitCaller) => {
      try {
        dispatch({ type: "UPDATE_BUILDING_INFO", updater: (r) => ({ ...r, status: "progress" }) });
        const response = await caller(formDataToSubmit, dtCreation);
        if (response?.job_id) {
          dispatch({ type: "UPDATE_BUILDING_INFO", updater: (r) => ({ ...r, status: "completed" }) });
          setJobId(response.job_id);
        } else {
          throw new Error(response?.error);
        }
      } catch (error) {
        const errorMessage =
          (error as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          (t("AddBuilding.submitModal.error.failedToSubmit") as string);
        dispatch({
          type: "UPDATE_BUILDING_INFO",
          updater: (r) => ({ ...r, errorMessage, status: "error" }),
        });
      }
    },
    [dispatch, setJobId, t]
  );

  const handleSubmitBase: HandleSubmitBase = useCallback(
    async ({ caller, dtCreation, shouldDeleteImages }) => {
      if (shouldAbortSubmitCall()) {
        toast.error(t("AddBuilding.submitModal.error.jobAlreadyInProgress"));
        return;
      }
      const { signal, submitDateTime } = prepareSubmit();
      if (shouldDeleteImages) deleteImages();
      const formDataWithUrls = await uploadFiles(signal, submitDateTime);
      if (!formDataWithUrls) return;
      if (signal.aborted) return;
      await submitToApi(formDataWithUrls, dtCreation, caller);
    },
    [shouldAbortSubmitCall, t, prepareSubmit, deleteImages, uploadFiles, submitToApi]
  );

  return { cancelInFlightUploads, handleSubmitBase };
};
